import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Instagram, Facebook } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_luxury-drape/artifacts/4nl3m339_Gemini_Generated_Image_2w5nsu2w5nsu2w5n-removebg-preview.png";

const menuItems = [
  { name: 'Home', href: '#home' },
  { name: 'Collections', href: '#collections' },
  { name: 'New Arrivals', href: '#new-arrivals' },
  { name: 'Ready-Made Sarees', href: '#sarees' },
  { name: 'Dress Materials', href: '#dress-materials' },
  { name: 'Ethnic Wear', href: '#ethnic-wear' },
  { name: 'About Us', href: '#about' },
  { name: 'Contact', href: '#contact' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <motion.header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-[#800020] shadow-lg py-3' 
            : 'bg-transparent py-6'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between">
            {/* Left Side - Social Icons (Desktop) */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href="https://www.instagram.com/devanandvestment/" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`transition-colors duration-300 ${isScrolled ? 'text-white/80 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/80 hover:text-[#D4AF37]'}`}
                data-testid="header-instagram-link"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="tel:+919535834444"
                className={`transition-colors duration-300 ${isScrolled ? 'text-white/80 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/80 hover:text-[#D4AF37]'}`}
                data-testid="header-phone-link"
              >
                <Phone size={20} />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className={isScrolled ? 'text-white' : 'text-[#1A1A1A]'} size={28} />
              ) : (
                <Menu className={isScrolled ? 'text-white' : 'text-[#1A1A1A]'} size={28} />
              )}
            </button>

            {/* Center Logo */}
            <motion.div 
              className="absolute left-1/2 transform -translate-x-1/2"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <a href="#home" onClick={() => scrollToSection('#home')} data-testid="header-logo">
                <img 
                  src={LOGO_URL} 
                  alt="Devanand Vestment" 
                  className={`h-12 md:h-16 object-contain transition-all duration-300 ${
                    isScrolled 
                      ? 'brightness-0 invert' 
                      : 'drop-shadow-[0_0_2px_rgba(255,255,255,0.8)]'
                  }`}
                  style={{
                    filter: isScrolled 
                      ? 'brightness(0) invert(1)' 
                      : 'drop-shadow(0 0 3px rgba(255,255,255,0.9)) drop-shadow(0 0 6px rgba(255,255,255,0.7))'
                  }}
                />
              </a>
            </motion.div>

            {/* Right Side Navigation (Desktop) */}
            <nav className="hidden lg:flex items-center gap-8">
              {menuItems.slice(0, 4).map((item) => (
                <motion.button
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  className={`relative font-body text-sm tracking-wide transition-colors duration-300 hover-underline ${
                    isScrolled ? 'text-white' : 'text-[#1A1A1A]'
                  }`}
                  whileHover={{ y: -2 }}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                </motion.button>
              ))}
            </nav>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
            <motion.div
              className="absolute right-0 top-0 h-full w-80 bg-[#800020] shadow-2xl"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
            >
              <div className="pt-24 px-8">
                <nav className="flex flex-col gap-6">
                  {menuItems.map((item, index) => (
                    <motion.button
                      key={item.name}
                      onClick={() => scrollToSection(item.href)}
                      className="text-white font-body text-lg text-left hover:text-[#D4AF37] transition-colors"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.name}
                    </motion.button>
                  ))}
                </nav>

                {/* Mobile Contact Info */}
                <div className="mt-12 pt-8 border-t border-white/20">
                  <a
                    href="https://wa.me/919535834444"
                    className="flex items-center gap-3 text-[#D4AF37] font-body"
                    data-testid="mobile-whatsapp-link"
                  >
                    <Phone size={20} />
                    <span>+91 95358 34444</span>
                  </a>
                  <a
                    href="https://www.instagram.com/devanandvestment/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-white/80 hover:text-[#D4AF37] font-body mt-4"
                    data-testid="mobile-instagram-link"
                  >
                    <Instagram size={20} />
                    <span>@devanandvestment</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
